import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [reports, setReports] = useState(null);
  const [farmers, setFarmers] = useState([]);
  const [expandedFarmer, setExpandedFarmer] = useState(null);
  const [farmerProfiles, setFarmerProfiles] = useState({});
  const [loading, setLoading] = useState(true);

  // Pool management state
  const [activeTab, setActiveTab] = useState('overview');
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolFarmers, setPoolFarmers] = useState([]);
  const [eligibleFarmers, setEligibleFarmers] = useState([]);
  const [showCreatePool, setShowCreatePool] = useState(false);
  const [newPool, setNewPool] = useState({ 
    name: '', 
    pool_type: 'biochar', 
    buffer_percentage: 20, 
    description: '',
    crediting_period_start: '',
    next_verification_due: ''
  });
  const [poolLoading, setPoolLoading] = useState(false);

  // Alerts state for verification deadlines and buffer reminders
  const [alerts, setAlerts] = useState(null);
  const [showEditPool, setShowEditPool] = useState(false);
  const [editPoolData, setEditPoolData] = useState(null);

  // Biochar management state
  const [allKilns, setAllKilns] = useState([]);
  const [allBurns, setAllBurns] = useState([]);
  const [biocharStats, setBiocharStats] = useState({});
  const [biocharLoading, setBiocharLoading] = useState(false);
  const [biocharFilter, setBiocharFilter] = useState({ county: '', status: '' });

  useEffect(() => {
    fetchReports();
    fetchAllFarmers();
    fetchPools();
    fetchAlerts();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/reports', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setReports(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const fetchAllFarmers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/farmers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setFarmers(data.farmers || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching farmers:', error);
      setLoading(false);
    }
  };

  const fetchFarmerProfile = async (email) => {
    if (farmerProfiles[email]) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/farmer/${email}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setFarmerProfiles(prev => ({ ...prev, [email]: data.profile }));
    } catch (error) {
      console.error('Error fetching farmer profile:', error);
    }
  };

  const toggleFarmer = (email) => {
    if (expandedFarmer === email) {
      setExpandedFarmer(null);
    } else {
      setExpandedFarmer(email);
      fetchFarmerProfile(email);
    }
  };

  const downloadCSV = () => {
    if (!reports?.csv_data) return;
    const blob = new Blob([reports.csv_data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agroconnect_report.csv';
    a.click();
  };

  const fetchPools = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/pools', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) setPools(data.pools || []);
    } catch (error) {
      console.error('Error fetching pools:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/pools/alerts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) setAlerts(data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const fetchPoolDetails = async (poolId) => {
    try {
      setPoolLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/pools/${poolId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) {
        setSelectedPool(data.pool);
        setPoolFarmers(data.farmers || []);
      }
    } catch (error) {
      console.error('Error fetching pool details:', error);
    } finally {
      setPoolLoading(false);
    }
  };

  const fetchEligibleFarmers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/farmers/eligible', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) setEligibleFarmers(data.farmers || []);
    } catch (error) {
      console.error('Error fetching eligible farmers:', error);
    }
  };

  const createPool = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/pools', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newPool, buffer_percentage: newPool.buffer_percentage / 100 })
      });
      const data = await response.json();
      if (data.ok) {
        alert('Pool created successfully!');
        setShowCreatePool(false);
        setNewPool({ name: '', pool_type: 'biochar', buffer_percentage: 20, description: '', crediting_period_start: '', next_verification_due: '' });
        fetchPools();
        fetchAlerts();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error creating pool:', error);
      alert('Failed to create pool');
    }
  };

  const updatePool = async () => {
    if (!editPoolData) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/pools/${editPoolData.pool_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editPoolData.name,
          status: editPoolData.status,
          buffer_percentage: editPoolData.buffer_percentage,
          verra_project_id: editPoolData.verra_project_id,
          description: editPoolData.description,
          last_verification_date: editPoolData.last_verification_date || null,
          next_verification_due: editPoolData.next_verification_due || null
        })
      });
      const data = await response.json();
      if (data.ok) {
        alert('Pool updated successfully!');
        setShowEditPool(false);
        setEditPoolData(null);
        fetchPools();
        fetchAlerts();
        if (selectedPool?.pool_id === data.pool.pool_id) setSelectedPool(data.pool);
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating pool:', error);
      alert('Failed to update pool');
    }
  };

  const exportPoolEvidence = async (poolId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/pools/${poolId}/evidence`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evidence_${poolId}_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting evidence:', error);
      alert('Failed to export evidence packet');
    }
  };

  const exportFarmerEvidence = async (farmerId, farmerName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/farmers/${farmerId}/evidence`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evidence_${farmerName || farmerId}_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting farmer evidence:', error);
      alert('Failed to export farmer evidence');
    }
  };

  const addFarmerToPool = async (farmerId) => {
    if (!selectedPool) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/pools/${selectedPool.pool_id}/farmers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ farmer_id: farmerId })
      });
      const data = await response.json();
      if (data.ok) {
        alert('Farmer added to pool!');
        fetchPoolDetails(selectedPool.pool_id);
        fetchEligibleFarmers();
        fetchPools();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error adding farmer:', error);
      alert('Failed to add farmer');
    }
  };

  const removeFarmerFromPool = async (farmerId) => {
    if (!selectedPool) return;
    if (!window.confirm('Remove this farmer from the pool?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/pools/${selectedPool.pool_id}/farmers/${farmerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) {
        alert('Farmer removed from pool');
        fetchPoolDetails(selectedPool.pool_id);
        fetchEligibleFarmers();
        fetchPools();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error removing farmer:', error);
      alert('Failed to remove farmer');
    }
  };

  // ========== BIOCHAR MANAGEMENT FUNCTIONS ==========
  const fetchBiocharData = async () => {
    try {
      setBiocharLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Build query params
      const params = new URLSearchParams();
      if (biocharFilter.county) params.append('county', biocharFilter.county);
      if (biocharFilter.status) params.append('status', biocharFilter.status);
      const queryStr = params.toString() ? `?${params.toString()}` : '';

      // Fetch kilns
      const kilnRes = await fetch(`/api/admin/kilns${queryStr}`, { headers });
      if (kilnRes.ok) {
        const kilnData = await kilnRes.json();
        setAllKilns(kilnData.kilns || []);
      }

      // Fetch burns
      const burnRes = await fetch(`/api/admin/burns${queryStr}`, { headers });
      if (burnRes.ok) {
        const burnData = await burnRes.json();
        setAllBurns(burnData.burns || []);
      }

      // Fetch coordinator stats
      const statsRes = await fetch(`/api/coordinator/stats${queryStr}`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setBiocharStats(statsData.stats || {});
      }
    } catch (err) {
      console.error('Error fetching biochar data:', err);
    } finally {
      setBiocharLoading(false);
    }
  };

  const verifyKiln = async (kilnId) => {
    if (!window.confirm('Verify this kiln?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/kilns/${kilnId}/verify`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) {
        alert('Kiln verified successfully!');
        fetchBiocharData();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (err) {
      console.error('Error verifying kiln:', err);
      alert('Failed to verify kiln');
    }
  };

  const verifyBurn = async (burnId) => {
    if (!window.confirm('Verify this burn?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/burns/${burnId}/verify`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.ok) {
        alert('Burn verified successfully!');
        fetchBiocharData();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (err) {
      console.error('Error verifying burn:', err);
      alert('Failed to verify burn');
    }
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const target = new Date(dateStr);
    const today = new Date();
    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  };

  const getAlertBadge = (daysLeft) => {
    if (daysLeft === null) return null;
    if (daysLeft < 0) return { color: '#ef4444', bg: '#fef2f2', text: `${Math.abs(daysLeft)} days overdue` };
    if (daysLeft <= 30) return { color: '#f59e0b', bg: '#fffbeb', text: `${daysLeft} days left` };
    if (daysLeft <= 90) return { color: '#3b82f6', bg: '#eff6ff', text: `${daysLeft} days left` };
    return { color: '#22c55e', bg: '#f0fdf4', text: `${daysLeft} days left` };
  };

  // Get unique counties from kilns for filter dropdown
  const uniqueCounties = [...new Set(allKilns.map(k => k.county).filter(Boolean))];

  return (
    <div style={{ minHeight: '100vh', background: '#0f2419', color: 'white', padding: '20px' }}>
      
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #2d5a45' }}>
        <div>
          <h1 style={{ margin: 0 }}>Admin Dashboard</h1>
          <p style={{ margin: '8px 0 0 0', color: '#888' }}>Logged in as: {user?.email} ({user?.role})</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => window.location.href = '/dashboard'} style={{ padding: '10px 20px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Farmer View</button>
          <button onClick={logout} style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Logout</button>
        </div>
      </header>

      {/* ALERTS BANNER */}
      {alerts && (alerts.counts?.overdue > 0 || alerts.counts?.upcoming_90_days > 0 || alerts.counts?.site_visits_30_days > 0) && (
        <div style={{ background: alerts.counts?.overdue > 0 ? '#7f1d1d' : '#78350f', padding: '16px 20px', borderRadius: '8px', marginBottom: '20px', border: `2px solid ${alerts.counts?.overdue > 0 ? '#ef4444' : '#f59e0b'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <strong style={{ color: alerts.counts?.overdue > 0 ? '#fecaca' : '#fef3c7' }}>Verification Alerts</strong>
              <div style={{ color: '#ddd', fontSize: '14px', marginTop: '4px' }}>
                {alerts.counts?.overdue > 0 && <span style={{ marginRight: '16px' }}>🔴 {alerts.counts.overdue} overdue verification{alerts.counts.overdue !== 1 ? 's' : ''}</span>}
                {alerts.counts?.upcoming_90_days > 0 && <span style={{ marginRight: '16px' }}>🟡 {alerts.counts.upcoming_90_days} due within 90 days</span>}
                {alerts.counts?.site_visits_30_days > 0 && <span>📍 {alerts.counts.site_visits_30_days} site visit{alerts.counts.site_visits_30_days !== 1 ? 's' : ''} within 30 days</span>}
              </div>
            </div>
            <button onClick={() => setActiveTab('pools')} style={{ marginLeft: 'auto', padding: '8px 16px', background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', cursor: 'pointer' }}>View Pools →</button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', flexWrap: 'wrap' }}>
        <button onClick={() => setActiveTab('overview')} style={{ padding: '12px 24px', background: activeTab === 'overview' ? '#4ade80' : 'transparent', color: activeTab === 'overview' ? '#0f2419' : '#4ade80', border: '1px solid #4ade80', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Overview</button>
        <button onClick={() => setActiveTab('farmers')} style={{ padding: '12px 24px', background: activeTab === 'farmers' ? '#4ade80' : 'transparent', color: activeTab === 'farmers' ? '#0f2419' : '#4ade80', border: '1px solid #4ade80', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>All Farmers ({farmers.length})</button>
        <button onClick={() => { setActiveTab('biochar'); fetchBiocharData(); }} style={{ padding: '12px 24px', background: activeTab === 'biochar' ? '#f59e0b' : 'transparent', color: activeTab === 'biochar' ? '#0f2419' : '#f59e0b', border: '1px solid #f59e0b', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
          🔥 Biochar
        </button>
        <button onClick={() => { setActiveTab('pools'); fetchEligibleFarmers(); }} style={{ padding: '12px 24px', background: activeTab === 'pools' ? '#4ade80' : 'transparent', color: activeTab === 'pools' ? '#0f2419' : '#4ade80', border: '1px solid #4ade80', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', position: 'relative' }}>
          🌱 Offset Pools ({pools.length})
          {alerts?.counts?.overdue > 0 && <span style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#ef4444', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{alerts.counts.overdue}</span>}
        </button>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            <section style={{ marginBottom: '40px' }}>
              <h2 style={{ marginBottom: '16px' }}>Platform Overview</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                <KPICard title="Farmers" value={reports?.kpis?.farmers || 0} />
                <KPICard title="Plots" value={reports?.kpis?.plots || 0} />
                <KPICard title="Crops" value={reports?.kpis?.crops || 0} />
                <KPICard title="Biochar Applications" value={reports?.kpis?.biochar || 0} />
                <KPICard title="Soil Samples" value={reports?.kpis?.soil || 0} />
                <KPICard title="Offset Pools" value={pools.length} />
              </div>
            </section>

            <section style={{ marginBottom: '40px' }}>
              <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45', textAlign: 'center' }}>
                <h3 style={{ marginTop: 0, color: '#4ade80' }}>Export Data</h3>
                <p style={{ color: '#888', marginBottom: '16px' }}>Download all platform data as CSV for analysis</p>
                <button onClick={downloadCSV} disabled={!reports?.csv_data} style={{ padding: '12px 24px', background: reports?.csv_data ? '#4ade80' : '#666', color: reports?.csv_data ? '#0f2419' : '#ccc', border: 'none', borderRadius: '4px', cursor: reports?.csv_data ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}>📊 Download CSV Report</button>
              </div>
            </section>

            {pools.length > 0 && (
              <section style={{ marginBottom: '40px' }}>
                <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
                  <h3 style={{ marginTop: 0, color: '#4ade80' }}>🌱 Offset Pools Summary</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                    {pools.slice(0, 4).map((pool) => {
                      const daysLeft = daysUntil(pool.next_verification_due);
                      const alertBadge = getAlertBadge(daysLeft);
                      return (
                        <div key={pool.pool_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '6px', border: '1px solid #2d5a45' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div style={{ color: '#4ade80', fontWeight: 'bold' }}>{pool.pool_type === 'biochar' ? '🔥' : '🌊'} {pool.name}</div>
                            <span style={{ padding: '2px 8px', background: pool.status === 'verified' ? '#22c55e' : pool.status === 'registered' ? '#3b82f6' : '#f59e0b', color: 'white', borderRadius: '4px', fontSize: '11px' }}>{pool.status?.replace('_', ' ').toUpperCase()}</span>
                          </div>
                          <div style={{ color: '#888', fontSize: '13px', marginBottom: '8px' }}>{pool.farmer_count || 0} farmers • {(pool.total_carbon || 0).toFixed(1)} t CO₂</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ color: '#888', fontSize: '12px' }}>Buffer:</span>
                            <div style={{ flex: 1, height: '8px', background: '#1a3327', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${(pool.buffer_percentage || 0.2) * 100}%`, height: '100%', background: '#fbbf24', borderRadius: '4px' }} />
                            </div>
                            <span style={{ color: '#fbbf24', fontSize: '12px', fontWeight: 'bold' }}>{((pool.buffer_percentage || 0.2) * 100).toFixed(0)}%</span>
                          </div>
                          {alertBadge && <div style={{ padding: '6px 10px', background: alertBadge.bg, color: alertBadge.color, borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>⏰ Next verification: {alertBadge.text}</div>}
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={() => setActiveTab('pools')} style={{ marginTop: '16px', padding: '10px 20px', background: 'transparent', color: '#4ade80', border: '1px solid #4ade80', borderRadius: '4px', cursor: 'pointer' }}>View All Pools →</button>
                </div>
              </section>
            )}
          </>
        )}

        {/* FARMERS TAB */}
        {activeTab === 'farmers' && (
          <section style={{ marginBottom: '40px' }}>
            <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
              <h2 style={{ marginTop: 0, marginBottom: '24px' }}>👨‍🌾 All Registered Farmers ({farmers.length})</h2>
              {loading ? <p style={{ color: '#888' }}>Loading farmers...</p> : (
                <div style={{ display: 'grid', gap: '16px' }}>
                  {farmers.map((farmer) => {
                    const profile = farmerProfiles[farmer.email];
                    const isExpanded = expandedFarmer === farmer.email;
                    return (
                      <div key={farmer.email} style={{ background: '#0f2419', borderRadius: '8px', border: '1px solid #2d5a45', overflow: 'hidden' }}>
                        <div onClick={() => toggleFarmer(farmer.email)} style={{ padding: '20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: '0 0 8px 0', color: '#4ade80' }}>
                              {farmer.full_name || farmer.email}
                              {farmer.is_offset_participant === 1 && <span style={{ marginLeft: '10px', padding: '2px 8px', background: '#22c55e', color: 'white', borderRadius: '4px', fontSize: '12px' }}>🌱 Offset</span>}
                            </h3>
                            <div style={{ color: '#888', fontSize: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                              <div>📧 {farmer.email}</div>
                              {farmer.country && <div>🌍 {farmer.country}</div>}
                              <div>📊 {farmer.plot_count} plots • {farmer.sample_count} samples</div>
                              <div>📅 Joined: {new Date(farmer.created_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {farmer.is_offset_participant === 1 && farmer.farmer_id && (
                              <button onClick={(e) => { e.stopPropagation(); exportFarmerEvidence(farmer.farmer_id, farmer.full_name || farmer.email); }} style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} title="Export evidence packet">📦 Export Evidence</button>
                            )}
                            <span style={{ color: '#888', fontSize: '24px' }}>{isExpanded ? '▼' : '▶'}</span>
                          </div>
                        </div>
                        {isExpanded && profile && (
                          <div style={{ padding: '20px', borderTop: '1px solid #2d5a45', background: '#1a3327' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                              <div>
                                <h4 style={{ margin: '0 0 12px 0', color: '#4ade80' }}>📍 Plots ({profile.plots?.length || 0})</h4>
                                {profile.plots?.length > 0 ? profile.plots.map((plot) => (
                                  <div key={plot.plot_id} style={{ background: '#0f2419', padding: '12px', borderRadius: '4px', marginBottom: '8px' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{plot.plot_name}</div>
                                    <div style={{ color: '#888', fontSize: '12px' }}>
                                      {plot.area_ha && `${plot.area_ha} ha • `}
                                      {plot.soil_type && `${plot.soil_type} • `}
                                      {plot.primary_crop && plot.primary_crop}
                                    </div>
                                  </div>
                                )) : <p style={{ color: '#888', fontSize: '14px' }}>No plots registered</p>}
                              </div>
                              <div>
                                <h4 style={{ margin: '0 0 12px 0', color: '#4ade80' }}>🧪 Soil Samples ({profile.samples?.length || 0})</h4>
                                {profile.samples?.length > 0 ? profile.samples.slice(0, 5).map((sample) => (
                                  <div key={sample.sample_id} style={{ background: '#0f2419', padding: '12px', borderRadius: '4px', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span>{sample.sample_date}</span>
                                      <span style={{ padding: '2px 8px', background: sample.verified ? '#22c55e' : '#f59e0b', color: 'white', borderRadius: '4px', fontSize: '11px' }}>{sample.verified ? 'Verified' : 'Pending'}</span>
                                    </div>
                                    <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                                      SOC: {sample.soil_organic_carbon_pct || 'N/A'}% • pH: {sample.ph || 'N/A'}
                                    </div>
                                  </div>
                                )) : <p style={{ color: '#888', fontSize: '14px' }}>No soil samples</p>}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ========== BIOCHAR TAB ========== */}
        {activeTab === 'biochar' && (
          <section style={{ marginBottom: '40px' }}>
            <div style={{ background: '#1a3327', padding: '24px', borderRadius: '12px', border: '2px solid #f59e0b' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#f59e0b' }}>🔥 Biochar to Soil Management</h2>
                  <p style={{ margin: '8px 0 0 0', color: '#888' }}>Manage kilns, verify burns, and track carbon credits</p>
                </div>
                <button onClick={fetchBiocharData} style={{ padding: '10px 20px', background: '#f59e0b', color: '#0f2419', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                  🔄 Refresh
                </button>
              </div>

              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4ade80' }}>{biocharStats.total_kilns || allKilns.length}</div>
                  <div style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase' }}>Total Kilns</div>
                </div>
                <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#22c55e' }}>{biocharStats.verified_kilns || allKilns.filter(k => k.status === 'verified').length}</div>
                  <div style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase' }}>Verified Kilns</div>
                </div>
                <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>{biocharStats.total_burns || allBurns.length}</div>
                  <div style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase' }}>Total Burns</div>
                </div>
                <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{biocharStats.completed_burns || allBurns.filter(b => b.status === 'complete').length}</div>
                  <div style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase' }}>Completed</div>
                </div>
                <div style={{ background: '#0f2419', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4ade80' }}>{(biocharStats.total_credits || allBurns.reduce((sum, b) => sum + (b.credits_earned || 0), 0)).toFixed(2)}</div>
                  <div style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase' }}>Credits Earned</div>
                </div>
              </div>

              {/* Filters */}
              <div style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ color: '#888' }}>Filters:</span>
                <select 
                  value={biocharFilter.county} 
                  onChange={(e) => setBiocharFilter(f => ({ ...f, county: e.target.value }))}
                  style={{ padding: '8px 12px', background: '#1a3327', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }}
                >
                  <option value="">All Counties</option>
                  {uniqueCounties.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select 
                  value={biocharFilter.status} 
                  onChange={(e) => setBiocharFilter(f => ({ ...f, status: e.target.value }))}
                  style={{ padding: '8px 12px', background: '#1a3327', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                </select>
                <button 
                  onClick={fetchBiocharData}
                  style={{ padding: '8px 16px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Apply
                </button>
              </div>

              {biocharLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading biochar data...</div>
              ) : (
                <>
                  {/* Pending Kilns Section */}
                  {allKilns.filter(k => k.status === 'pending').length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#f59e0b', marginBottom: '12px' }}>⏳ Pending Kiln Verifications ({allKilns.filter(k => k.status === 'pending').length})</h3>
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {allKilns.filter(k => k.status === 'pending').map(kiln => (
                          <div key={kiln.kiln_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                  <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{kiln.kiln_id}</span>
                                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold', background: '#f59e0b', color: 'white' }}>pending</span>
                                </div>
                                <div style={{ color: '#888', fontSize: '13px' }}>
                                  👤 {kiln.farmer_name || 'Unknown'} • 📧 {kiln.farmer_email}
                                </div>
                                <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                                  {kiln.kiln_type} • 📍 {kiln.county || 'No county'} • Registered {kiln.registration_date}
                                </div>
                                <div style={{ color: '#888', fontSize: '11px', marginTop: '4px', fontFamily: 'monospace' }}>
                                  Kiln GPS: {kiln.gps_lat?.toFixed(5)}, {kiln.gps_lng?.toFixed(5)}
                                </div>
                              </div>
                              <button
                                onClick={() => verifyKiln(kiln.kiln_id)}
                                style={{ padding: '10px 20px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                              >
                                ✓ Verify Kiln
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All Kilns Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#4ade80', marginBottom: '12px' }}>🔥 All Kilns ({allKilns.length})</h3>
                    {allKilns.length === 0 ? (
                      <div style={{ background: '#0f2419', padding: '24px', borderRadius: '8px', textAlign: 'center', color: '#888' }}>
                        No kilns registered yet
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {allKilns.map(kiln => (
                          <div key={kiln.kiln_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                  <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{kiln.kiln_id}</span>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold',
                                    background: kiln.status === 'verified' ? '#22c55e' : '#f59e0b', color: 'white'
                                  }}>{kiln.status}</span>
                                </div>
                                <div style={{ color: '#888', fontSize: '13px' }}>
                                  👤 {kiln.farmer_name || 'Unknown'} • 📧 {kiln.farmer_email}
                                </div>
                                <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                                  {kiln.kiln_type} • 📍 {kiln.county || 'No county'} • Registered {kiln.registration_date}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                {kiln.status === 'pending' && (
                                  <button onClick={() => verifyKiln(kiln.kiln_id)} style={{ padding: '6px 12px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                                    ✓ Verify
                                  </button>
                                )}
                                {kiln.status === 'verified' && kiln.verified_at && (
                                  <div style={{ color: '#22c55e', fontSize: '11px' }}>
                                    Verified {new Date(kiln.verified_at).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Completed Burns Needing Verification */}
                  {allBurns.filter(b => b.status === 'complete' && !b.verified).length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#f59e0b', marginBottom: '12px' }}>✅ Burns Ready for Verification ({allBurns.filter(b => b.status === 'complete' && !b.verified).length})</h3>
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {allBurns.filter(b => b.status === 'complete' && !b.verified).map(burn => {
                          const steps = JSON.parse(burn.steps_completed || '[]');
                          return (
                            <div key={burn.burn_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '8px', border: '1px solid #22c55e' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                    <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{burn.burn_id}</span>
                                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold', background: '#22c55e', color: 'white' }}>complete</span>
                                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>{burn.credits_earned?.toFixed(2)} credits</span>
                                  </div>
                                  <div style={{ color: '#888', fontSize: '13px' }}>
                                    👤 {burn.farmer_name || 'Unknown'} • 🔥 {burn.kiln_id}
                                  </div>
                                  <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                                    📅 {burn.burn_date} • {burn.biomass_quantity} {burn.biomass_unit} of {burn.biomass_type}
                                  </div>
                                  <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                                    {[1,2,3,4].map(s => (
                                      <div key={s} style={{
                                        width: '24px', height: '24px', borderRadius: '50%',
                                        background: steps.includes(`step${s}`) ? '#4ade80' : '#2d5a45',
                                        color: steps.includes(`step${s}`) ? '#0f2419' : '#888',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '11px', fontWeight: 'bold'
                                      }}>{steps.includes(`step${s}`) ? '✓' : s}</div>
                                    ))}
                                  </div>
                                  
                                  {/* Photo Evidence */}
                                  {(burn.burn_photo_url || burn.application_photo_url) && (
                                    <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                      {burn.burn_photo_url && (
                                        <div style={{ textAlign: 'center' }}>
                                          <img 
                                            src={burn.burn_photo_url} 
                                            alt="Burn photo" 
                                            style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #f59e0b', cursor: 'pointer' }}
                                            onClick={() => window.open(burn.burn_photo_url, '_blank')}
                                          />
                                          <div style={{ color: '#f59e0b', fontSize: '10px', marginTop: '4px' }}>Step 2: Burn</div>
                                        </div>
                                      )}
                                      {burn.application_photo_url && (
                                        <div style={{ textAlign: 'center' }}>
                                          <img 
                                            src={burn.application_photo_url} 
                                            alt="Application photo" 
                                            style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #4ade80', cursor: 'pointer' }}
                                            onClick={() => window.open(burn.application_photo_url, '_blank')}
                                          />
                                          <div style={{ color: '#4ade80', fontSize: '10px', marginTop: '4px' }}>Step 4: Soil</div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => verifyBurn(burn.burn_id)}
                                  style={{ padding: '10px 20px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                  ✓ Verify Burn
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* All Burns */}
                  <div>
                    <h3 style={{ color: '#3b82f6', marginBottom: '12px' }}>📋 All Burns ({allBurns.length})</h3>
                    {allBurns.length === 0 ? (
                      <div style={{ background: '#0f2419', padding: '24px', borderRadius: '8px', textAlign: 'center', color: '#888' }}>
                        No burns logged yet
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {allBurns.map(burn => {
                          const steps = JSON.parse(burn.steps_completed || '[]');
                          return (
                            <div key={burn.burn_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                    <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{burn.burn_id}</span>
                                    <span style={{
                                      padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold',
                                      background: burn.status === 'complete' ? '#22c55e' : '#f59e0b', color: 'white'
                                    }}>{burn.status}</span>
                                    {burn.verified && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold', background: '#3b82f6', color: 'white' }}>verified</span>}
                                  </div>
                                  <div style={{ color: '#888', fontSize: '13px' }}>
                                    👤 {burn.farmer_name || 'Unknown'} • 🔥 {burn.kiln_id}
                                  </div>
                                  <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                                    📅 {burn.burn_date} • {burn.biomass_quantity} {burn.biomass_unit} of {burn.biomass_type}
                                  </div>
                                  
                                  {/* Photo Evidence */}
                                  {(burn.burn_photo_url || burn.application_photo_url) && (
                                    <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                      {burn.burn_photo_url && (
                                        <div style={{ textAlign: 'center' }}>
                                          <img 
                                            src={burn.burn_photo_url} 
                                            alt="Burn photo" 
                                            style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '2px solid #f59e0b', cursor: 'pointer' }}
                                            onClick={() => window.open(burn.burn_photo_url, '_blank')}
                                          />
                                          <div style={{ color: '#f59e0b', fontSize: '9px', marginTop: '2px' }}>Burn</div>
                                        </div>
                                      )}
                                      {burn.application_photo_url && (
                                        <div style={{ textAlign: 'center' }}>
                                          <img 
                                            src={burn.application_photo_url} 
                                            alt="Application photo" 
                                            style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '2px solid #4ade80', cursor: 'pointer' }}
                                            onClick={() => window.open(burn.application_photo_url, '_blank')}
                                          />
                                          <div style={{ color: '#4ade80', fontSize: '9px', marginTop: '2px' }}>Soil</div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                                    {[1,2,3,4].map(s => (
                                      <div key={s} style={{
                                        width: '20px', height: '20px', borderRadius: '50%',
                                        background: steps.includes(`step${s}`) ? '#4ade80' : '#2d5a45',
                                        color: steps.includes(`step${s}`) ? '#0f2419' : '#888',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '10px', fontWeight: 'bold'
                                      }}>{s}</div>
                                    ))}
                                  </div>
                                  {burn.credits_earned > 0 && (
                                    <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: '14px' }}>
                                      {burn.credits_earned.toFixed(2)} credits
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* POOLS TAB */}
        {activeTab === 'pools' && (
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>🌱 Offset Pool Management</h2>
              <button onClick={() => setShowCreatePool(true)} style={{ padding: '12px 24px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>+ Create New Pool</button>
            </div>

            {/* Create Pool Modal */}
            {showCreatePool && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div style={{ background: '#1a3327', padding: '32px', borderRadius: '12px', maxWidth: '500px', width: '90%' }}>
                  <h3 style={{ marginTop: 0, color: '#4ade80' }}>Create New Offset Pool</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Pool Name *</label>
                      <input type="text" value={newPool.name} onChange={(e) => setNewPool({ ...newPool, name: e.target.value })} placeholder="e.g., Kenya Biochar 2024" style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Pool Type *</label>
                      <select value={newPool.pool_type} onChange={(e) => setNewPool({ ...newPool, pool_type: e.target.value })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }}>
                        <option value="biochar">🔥 Biochar</option>
                        <option value="blue_carbon">🌊 Blue Carbon</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Buffer Percentage (%)</label>
                      <input type="number" value={newPool.buffer_percentage} onChange={(e) => setNewPool({ ...newPool, buffer_percentage: parseFloat(e.target.value) || 0 })} min="0" max="50" style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Crediting Period Start</label>
                      <input type="date" value={newPool.crediting_period_start} onChange={(e) => setNewPool({ ...newPool, crediting_period_start: e.target.value })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Next Verification Due</label>
                      <input type="date" value={newPool.next_verification_due} onChange={(e) => setNewPool({ ...newPool, next_verification_due: e.target.value })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Description</label>
                      <textarea value={newPool.description} onChange={(e) => setNewPool({ ...newPool, description: e.target.value })} rows="3" placeholder="Describe the pool..." style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white', resize: 'vertical' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={createPool} style={{ flex: 1, padding: '12px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Create Pool</button>
                      <button onClick={() => setShowCreatePool(false)} style={{ flex: 1, padding: '12px', background: 'transparent', color: '#888', border: '1px solid #2d5a45', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Pool Modal */}
            {showEditPool && editPoolData && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div style={{ background: '#1a3327', padding: '32px', borderRadius: '12px', maxWidth: '500px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                  <h3 style={{ marginTop: 0, color: '#4ade80' }}>Edit Pool: {editPoolData.name}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Pool Name</label>
                      <input type="text" value={editPoolData.name || ''} onChange={(e) => setEditPoolData({ ...editPoolData, name: e.target.value })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Status</label>
                      <select value={editPoolData.status || 'under_development'} onChange={(e) => setEditPoolData({ ...editPoolData, status: e.target.value })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }}>
                        <option value="under_development">Under Development</option>
                        <option value="under_validation">Under Validation</option>
                        <option value="registered">Registered</option>
                        <option value="verified">Verified</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Buffer Percentage (decimal, e.g. 0.20)</label>
                      <input type="number" step="0.01" value={editPoolData.buffer_percentage || 0.2} onChange={(e) => setEditPoolData({ ...editPoolData, buffer_percentage: parseFloat(e.target.value) || 0.2 })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Verra Project ID</label>
                      <input type="text" value={editPoolData.verra_project_id || ''} onChange={(e) => setEditPoolData({ ...editPoolData, verra_project_id: e.target.value })} placeholder="e.g., VCS-1234" style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Description</label>
                      <textarea value={editPoolData.description || ''} onChange={(e) => setEditPoolData({ ...editPoolData, description: e.target.value })} rows="3" style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white', resize: 'vertical' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Last Verification Date</label>
                      <input type="date" value={editPoolData.last_verification_date || ''} onChange={(e) => setEditPoolData({ ...editPoolData, last_verification_date: e.target.value })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#888', marginBottom: '6px' }}>Next Verification Due</label>
                      <input type="date" value={editPoolData.next_verification_due || ''} onChange={(e) => setEditPoolData({ ...editPoolData, next_verification_due: e.target.value })} style={{ width: '100%', padding: '10px', background: '#0f2419', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={updatePool} style={{ padding: '12px 24px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Save Changes</button>
                      <button onClick={() => { setShowEditPool(false); setEditPoolData(null); }} style={{ padding: '12px 24px', background: 'transparent', color: '#888', border: '1px solid #2d5a45', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pools List */}
            <div style={{ display: 'grid', gap: '16px' }}>
              {pools.length === 0 ? (
                <div style={{ background: '#1a3327', padding: '40px', borderRadius: '8px', textAlign: 'center', color: '#888' }}>No offset pools created yet. Click "Create New Pool" to get started.</div>
              ) : (
                pools.map((pool) => {
                  const daysLeft = daysUntil(pool.next_verification_due);
                  const alertBadge = getAlertBadge(daysLeft);
                  return (
                    <div key={pool.pool_id} style={{ background: selectedPool?.pool_id === pool.pool_id ? '#1a3327' : '#0f2419', padding: '20px', borderRadius: '8px', border: selectedPool?.pool_id === pool.pool_id ? '2px solid #4ade80' : '1px solid #2d5a45', cursor: 'pointer' }}>
                      <div onClick={() => fetchPoolDetails(pool.pool_id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: '0 0 8px 0', color: '#4ade80' }}>{pool.pool_type === 'biochar' ? '🔥' : '🌊'} {pool.name}</h3>
                          <div style={{ color: '#888', fontSize: '14px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ padding: '2px 8px', background: pool.status === 'verified' ? '#22c55e' : pool.status === 'registered' ? '#3b82f6' : '#f59e0b', color: 'white', borderRadius: '4px', fontSize: '12px' }}>{pool.status?.replace('_', ' ').toUpperCase()}</span>
                            <span>Buffer: {((pool.buffer_percentage || 0.2) * 100).toFixed(0)}%</span>
                            {pool.pool_type === 'blue_carbon' && <span style={{ color: '#60a5fa' }}>Blue Carbon</span>}
                          </div>
                          {alertBadge && <div style={{ marginTop: '12px', padding: '8px 12px', background: alertBadge.bg, color: alertBadge.color, borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', display: 'inline-block' }}>⏰ Next verification: {alertBadge.text}</div>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4ade80' }}>{pool.farmer_count || 0}</div>
                          <div style={{ color: '#888', fontSize: '12px' }}>farmers</div>
                        </div>
                      </div>
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #2d5a45', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button onClick={(e) => { e.stopPropagation(); fetchPoolDetails(pool.pool_id); }} style={{ padding: '6px 12px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>View Farmers</button>
                        <button onClick={(e) => { e.stopPropagation(); setEditPoolData(pool); setShowEditPool(true); }} style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>✏️ Edit Pool</button>
                        <button onClick={(e) => { e.stopPropagation(); exportPoolEvidence(pool.pool_id); }} style={{ padding: '6px 12px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} title="Export all evidence for validator review">📦 Export Evidence Packet</button>
                      </div>
                      {pool.total_carbon > 0 && <div style={{ marginTop: '12px', color: '#888', fontSize: '14px' }}>Total Carbon: <strong style={{ color: '#4ade80' }}>{pool.total_carbon?.toFixed(2) || 0} tonnes CO₂</strong></div>}
                    </div>
                  );
                })
              )}
            </div>

            {/* Selected Pool Details */}
            {selectedPool && (
              <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45', marginTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, color: '#4ade80' }}>{selectedPool.pool_type === 'biochar' ? '🔥' : '🌊'} {selectedPool.name} - Farmers ({poolFarmers.length})</h3>
                  <button onClick={() => { setSelectedPool(null); setPoolFarmers([]); }} style={{ padding: '8px 16px', background: 'transparent', color: '#888', border: '1px solid #2d5a45', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px', padding: '16px', background: '#0f2419', borderRadius: '6px' }}>
                  <div><div style={{ color: '#888', fontSize: '12px' }}>Pool Type</div><div style={{ color: '#ddd', fontWeight: 'bold' }}>{selectedPool.pool_type === 'biochar' ? '🔥 Biochar' : '🌊 Blue Carbon'}</div></div>
                  <div><div style={{ color: '#888', fontSize: '12px' }}>Buffer %</div><div style={{ color: '#fbbf24', fontWeight: 'bold' }}>{((selectedPool.buffer_percentage || 0.2) * 100).toFixed(0)}%</div></div>
                  <div><div style={{ color: '#888', fontSize: '12px' }}>Status</div><div style={{ color: '#4ade80', fontWeight: 'bold' }}>{selectedPool.status?.replace('_', ' ').toUpperCase()}</div></div>
                  {selectedPool.verra_project_id && <div><div style={{ color: '#888', fontSize: '12px' }}>Verra ID</div><div style={{ color: '#ddd', fontWeight: 'bold' }}>{selectedPool.verra_project_id}</div></div>}
                </div>

                {eligibleFarmers.length > 0 && (
                  <div style={{ marginBottom: '20px', padding: '16px', background: '#0f2419', borderRadius: '6px' }}>
                    <label style={{ display: 'block', color: '#888', marginBottom: '8px' }}>Add Farmer to Pool:</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <select id="farmerSelect" style={{ flex: 1, padding: '10px', background: '#1a3327', border: '1px solid #2d5a45', borderRadius: '4px', color: 'white' }}>
                        <option value="">Select a farmer...</option>
                        {eligibleFarmers.map((f) => <option key={f.farmer_id} value={f.farmer_id}>{f.full_name || f.email} ({f.country || 'No location'})</option>)}
                      </select>
                      <button onClick={() => { const select = document.getElementById('farmerSelect'); if (select.value) addFarmerToPool(select.value); }} style={{ padding: '10px 20px', background: '#4ade80', color: '#0f2419', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Add</button>
                    </div>
                  </div>
                )}

                {poolLoading ? <p style={{ color: '#888' }}>Loading...</p> : poolFarmers.length === 0 ? <p style={{ color: '#888' }}>No farmers in this pool yet.</p> : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {poolFarmers.map((farmer) => (
                      <div key={farmer.farmer_id} style={{ background: '#0f2419', padding: '16px', borderRadius: '6px', border: '1px solid #2d5a45' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ color: '#4ade80', fontWeight: 'bold', marginBottom: '8px' }}>{farmer.full_name || farmer.email}</div>
                            <div style={{ color: '#888', fontSize: '13px' }}>📍 {[farmer.district, farmer.region, farmer.country].filter(Boolean).join(', ') || 'No location'}</div>
                            <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>📧 {farmer.email}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#4ade80', fontWeight: 'bold' }}>{farmer.carbon_tonnes_contributed?.toFixed(2) || '0.00'} t CO₂</div>
                            <div style={{ color: '#888', fontSize: '12px' }}>{farmer.vcus_allocated || 0} VCUs</div>
                            <div style={{ color: '#888', fontSize: '12px' }}>${farmer.expected_payment_usd?.toFixed(2) || '0.00'} expected</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #2d5a45' }}>
                          <div style={{ fontSize: '12px', color: '#888' }}>
                            <span style={{ padding: '2px 8px', background: farmer.membership_status === 'active' ? '#22c55e' : '#f59e0b', color: 'white', borderRadius: '4px', marginRight: '8px' }}>{farmer.membership_status || 'pending'}</span>
                            Joined: {new Date(farmer.joined_at).toLocaleDateString()}
                            {farmer.next_site_visit && <span style={{ marginLeft: '12px', color: '#60a5fa' }}>📍 Next visit: {new Date(farmer.next_site_visit).toLocaleDateString()}</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={(e) => { e.stopPropagation(); exportFarmerEvidence(farmer.farmer_id, farmer.full_name || farmer.email); }} style={{ padding: '6px 12px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>📦 Evidence</button>
                            <button onClick={(e) => { e.stopPropagation(); removeFarmerFromPool(farmer.farmer_id); }} style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Remove</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ title, value }) {
  return (
    <div style={{ background: '#1a3327', padding: '20px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
      <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4ade80' }}>{value}</div>
    </div>
  );
}